import { FeatureTourViewerTopic } from "./feature-tour-data";

/** The status of a feature tour topic for the current user. */
export type FeatureTourViewStatus = "unseen" | "skipped" | "completed";

/** The status of every feature tour topic the user has already interacted with. */
export type FeatureTourStatuses = Partial<
  Record<FeatureTourViewerTopic, FeatureTourViewStatus>
>;

/**
 * The FUE (first user experience) flags of a user.
 *
 * The Core API stores all flags of a user in a single JSON blob, which is shared across all
 * FARO applications. Writing replaces the entire blob, so the flags owned by other applications
 * need to be preserved as they are.
 */
export type FueFlags = {
  /** The status of the feature tour topics owned by the Viewer */
  known: FeatureTourStatuses;

  /** All flags owned by other applications, kept verbatim so they survive a write */
  other: Record<string, unknown>;
};

const VIEWER_TOPICS: readonly string[] = Object.values(FeatureTourViewerTopic);

/**
 * Splits the FUE flags of a user.
 *
 * @param fueFlags The FUE flags as stored by the Core API
 * @returns The parsed flags, split into the ones owned by the Viewer and all other ones
 */
export function splitFueFlags(fueFlags?: object | undefined): FueFlags {
  const flags: FueFlags = { known: {}, other: {} };
  if (!fueFlags) return flags;

  for (const [key, value] of Object.entries(fueFlags)) {
    if (isViewerTopic(key)) {
      if (isViewStatus(value)) {
        flags.known[key] = value;
      }
    } else {
      flags.other[key] = value;
    }
  }

  return flags;
}

/**
 * @param flags The FUE flags of a user
 * @returns The flags as a single object for the Core API, including the flags of other applications
 */
export function flattenFueFlags({
  known,
  other,
}: FueFlags): Record<string, unknown> {
  return { ...other, ...known };
}

/**
 * @param key The key of a FUE flag
 * @returns `true` if the key belongs to a feature tour topic of the Viewer
 */
function isViewerTopic(key: string): key is FeatureTourViewerTopic {
  return VIEWER_TOPICS.includes(key);
}

/**
 * @param value The value of a FUE flag
 * @returns `true` if the value is a valid feature tour view status
 */
function isViewStatus(value: unknown): value is FeatureTourViewStatus {
  return value === "unseen" || value === "skipped" || value === "completed";
}
